import React, { useState, useEffect } from 'react';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import QuotesList from './QuotesList';
import ViewQuote from './ViewQuote';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './Quotes.css';

function Quotations({ initialQuoteId, newQuoteRequest }) {
  const { goToSalesOrder } = useAppNavigate();
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'view', 'edit', 'create'
  const [createProductId, setCreateProductId] = useState(null);

  // Handle initial quote ID from cross-module navigation
  useEffect(() => {
    if (initialQuoteId) {
      setSelectedQuoteId(parseInt(initialQuoteId));
      setViewMode('view');
    }
  }, [initialQuoteId]);

  // New quote requested from another screen (e.g. "Create Quotation" at the end of the product wizard)
  useEffect(() => {
    if (newQuoteRequest) {
      setSelectedQuoteId(null);
      setCreateProductId(newQuoteRequest.productId || null);
      setViewMode('create');
    }
  }, [newQuoteRequest]);

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: 'Quotations',
          buttons: [
            {
              label: 'New Quote',
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: 'Refresh',
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: 'View Quote',
          showBack: true,
          onBack: handleBack,
          buttons: [
            {
              label: 'Edit',
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: 'Delete',
              icon: '🗑️',
              onClick: handleDelete,
              variant: 'danger'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: 'Edit Quote',
          showBack: false,
          buttons: [
            {
              label: 'Cancel',
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: 'Save',
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: 'New Quote',
          showBack: false,
          buttons: [
            {
              label: 'Cancel',
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: 'Create',
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode]);

  const handleSelectQuote = (quoteId) => {
    setSelectedQuoteId(quoteId);
    setViewMode('view');
  };

  const handleConvertQuote = (salesOrderId) => {
    goToSalesOrder(salesOrderId);
  };

  const handleNew = () => {
    setSelectedQuoteId(null);
    setCreateProductId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewQuote component
    if (window.viewQuoteRef && window.viewQuoteRef.handleDelete) {
      window.viewQuoteRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewQuote component
    if (window.viewQuoteRef && window.viewQuoteRef.handleSave) {
      window.viewQuoteRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedQuoteId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedQuoteId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in QuotesList
    if (window.quotesListRef && window.quotesListRef.fetchQuotes) {
      window.quotesListRef.fetchQuotes();
    }
  };

  const handleQuoteSaved = (quote) => {
    setSelectedQuoteId(quote.id);
    setViewMode('view');
    // Refresh the list
    if (window.quotesListRef && window.quotesListRef.fetchQuotes) {
      window.quotesListRef.fetchQuotes();
    }
  };

  return (
    <div className="quotations-page">
      <ResizablePanels
        leftPanel={
          <QuotesList
            ref={ref => window.quotesListRef = ref}
            onSelectQuote={handleSelectQuote}
            selectedQuoteId={selectedQuoteId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewQuote
              ref={ref => window.viewQuoteRef = ref}
              quoteId={selectedQuoteId}
              onBack={handleBack}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              initialProductId={viewMode === 'create' ? createProductId : null}
              onSave={handleQuoteSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
              onConvertQuote={handleConvertQuote}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default Quotations;